import { html, css } from 'lit';
import { classMap } from 'lit/directives/class-map';
import KaiElement from '../base';
class DialogElement extends KaiElement {
  static get is() {
    return 'kai-dialog';
  }

  static get properties() {
    return {
      dialogtitle: { type: String },
      message: { type: String },
      primarybtntext: { type: String },
      secondarybtntext: { type: String },
      primarybtndisabled: {
        type: Boolean,
        attribute: 'primarybtndisabled',
        reflect: true,
      },
      secondarybtndisabled: {
        type: Boolean,
        attribute: 'secondarybtndisabled',
        reflect: true,
      },
      open: { type: Boolean, attribute: 'open', reflect: true },
      // internal properties
      isOverflow: { type: Boolean, reflect: false },
      visibleIndicator: { type: Boolean, reflect: false },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .container {
          width: 100%;
          height: 100%;
          background-color: var(--dialog-background-color);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          z-index: var(--z-index-dialog);
          position: fixed;
          top: 0;
          left: 0;
        }

        .container.show {
          animation:
            scale-down 0.18s forwards cubic-bezier(0, 0, 0, 1),
            fade-in 0.12s forwards linear;
        }

        .dialog {
          width: 30rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        .title {
          width: 30rem;
          min-height: 3.2rem;
          padding: 0 2rem;
          margin-bottom: 1.2rem;
          box-sizing: border-box;
          color: var(--color-gs100);
          text-align: center;
          word-wrap: break-word;
        }

        .wrapper {
          width: 30rem;
          overflow: hidden;
          border-radius: 3rem;
          border: solid 0.2rem var(--color-gs80);
          position: relative;
        }

        .content {
          max-height: 32rem;
          /* set width to 30.5rem so that the total width still stay in 30rem
          except hidden vertical scroll bar height(0.5rem) */
          width: 30.5rem;
          padding-right: 0.5rem;
          padding-left: 0;
          color: var(--color-gs100);
          overflow-y: auto;
          overflow-x: hidden;
          box-sizing: border-box;
          background-color: var(--color-gs00);
        }

        .content::-webkit-scrollbar {
          display: none;
        }

        .content:-moz-dir(rtl) {
          padding-left: 0.5rem;
          padding-right: 0;
        }

        .content .message {
          padding: 2.5rem 2rem;
          text-align: center;
          word-wrap: break-word;
        }

        .indicator {
          /* 3rem * 2 for wrapper's border-radius */
          height: calc(32rem - 6rem);
          width: 0.2rem;
          border-radius: 0.1rem;
          position: absolute;
          right: 0.4rem;
          left: unset;
          top: 0;
          /* the same as wrapper's border-radius */
          margin: 3rem 0;
          overflow: hidden;
          background-color: var(--color-gs20);
          opacity: 0;
          animation-name: fade-out;
          animation-duration: 0.3s;
          animation-fill-mode: forwards;
          animation-timing-function: cubic-bezier(0, 0, 0.25, 1);
        }

        .indicator:-moz-dir(rtl) {
          right: unset;
          left: 0.4rem;
        }

        .indicator.visible {
          animation-name: fade-in;
          animation-duration: 0.3s;
          animation-fill-mode: forwards;
          animation-timing-function: cubic-bezier(0, 0, 0.25, 1);
        }

        @keyframes fade-in {
          0% {
            opacity: 0;
          }

          100% {
            opacity: 1;
          }
        }

        @keyframes fade-out {
          0% {
            opacity: 1;
          }

          100% {
            opacity: 0;
          }
        }

        @keyframes scale-down {
          0% {
            transform: scale(1.1);
          }

          100% {
            transform: scale(1);
          }
        }

        .indicator .bar {
          width: 100%;
          height: 30%;
          border-radius: 0.1rem;
          background-color: var(--theme-color);
          position: absolute;
          top: 0;
          scroll-behavior: smooth;
        }

        .buttons {
          width: 100%;
          display: flex;
          align-content: center;
          justify-content: space-between;
          margin-top: 3rem;
        }

        /* center button when there is only one button in dialog */
        .buttons > :only-child {
          margin: 0 auto;
          --pillbutton-width: 100%;
          max-width: 30rem;
          min-width: 14rem;
        }

        .hide {
          display: none;
        }

        kai-pillbutton {
          --pillbutton-width: 14rem;
        }

        @media (orientation: landscape) {
          .dialog {
            width: 50%;
          }

          /* landscape force to single line */
          .title {
            width: 100%;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }

          .wrapper {
            width: 100%;
          }

          .content {
            max-height: 15rem;
            /* set width to 100% + 0.5rem so that the total width still stay in
              100% except hidden vertical scroll bar height(0.5rem) */
            width: calc(100% + 0.5rem);
          }

          .indicator {
            /* 3rem * 2 for wrapper's border-radius */
            height: calc(15rem - 6rem);
          }

          .buttons > :only-child {
            max-width: 100%;
            min-width: 15rem;
          }

          .buttons {
            margin-top: 2rem;
          }

          kai-pillbutton {
            --pillbutton-width: 15rem;
          }
        }
      `,
    ];
  }

  constructor() {
    super();
    this.dialogtitle = '';
    this.open = false;
    this.isOverflow = false;
    this.visibleIndicator = false;
    this._updateIndicatorVisibility =
      this._updateIndicatorVisibility.bind(this);
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      if (this.open) {
        this._updateIndicatorVisibility();
        window.addEventListener('resize', this._updateIndicatorVisibility);
      } else {
        window.removeEventListener('resize', this._updateIndicatorVisibility);
      }
    }
  }

  _updateIndicatorVisibility() {
    const scrollTopMax = this.content && this.content.scrollTopMax;
    if (scrollTopMax) {
      this.isOverflow = true;
      this._fadeInIndicator();
      this._fadeOutIndicator();
    } else {
      this.isOverflow = false;
    }
  }

  _clickHandler(e) {
    if (e.target.hasAttribute('disabled')) {
      return;
    }

    e.stopPropagation();

    switch (e.target.id) {
      case 'secondarybtn':
        {
          const event = new CustomEvent('click', {
            detail: {
              type: 'secondary',
            },
            bubbles: true,
            composed: true,
          });
          this.dispatchEvent(event);
        }
        break;
      case 'primarybtn':
        {
          const event = new CustomEvent('click', {
            detail: {
              type: 'primary',
            },
            bubbles: true,
            composed: true,
          });
          this.dispatchEvent(event);
        }
        break;
      default:
        break;
    }
  }

  getButtons() {
    if (this.secondarybtntext && this.primarybtntext) {
      return html`
        <kai-pillbutton
          id="secondarybtn"
          text=${this.secondarybtntext}
          level="secondary"
          ?disabled="${this.secondarybtndisabled}"
          @click="${this._clickHandler}"
        >
        </kai-pillbutton>
        <kai-pillbutton
          id="primarybtn"
          text=${this.primarybtntext}
          ?disabled="${this.primarybtndisabled}"
          @click="${this._clickHandler}"
        ></kai-pillbutton>
      `;
    } else if (this.secondarybtntext) {
      return html`
        <kai-pillbutton
          id="secondarybtn"
          text=${this.secondarybtntext}
          level="secondary"
          ?disabled="${this.secondarybtndisabled}"
          @click="${this._clickHandler}"
        ></kai-pillbutton>
      `;
    } else if (this.primarybtntext) {
      return html`
        <kai-pillbutton
          id="primarybtn"
          text=${this.primarybtntext}
          ?disabled="${this.primarybtndisabled}"
          @click="${this._clickHandler}"
        ></kai-pillbutton>
      `;
    } else {
      return '';
    }
  }

  get content() {
    return this.shadowRoot.querySelector('.content');
  }

  get indicator() {
    return this.shadowRoot.querySelector('.indicator');
  }

  get bar() {
    return this.shadowRoot.querySelector('.bar');
  }

  _onScroll() {
    const scrollTop = this.content.scrollTop;
    const scrollTopMax = this.content.scrollTopMax;
    const ratio = scrollTop / scrollTopMax;
    const offsetHeight = this.content.offsetHeight;
    const barHeight = this.bar.offsetHeight;
    let position = offsetHeight * ratio;
    const rootFontSize = getComputedStyle(
      document.documentElement
    ).getPropertyValue('font-size');
    /*
      calculate indicator bar's top position,
      which is based on px, so convert unit from rem to px here.
    */
    const limit = 3 * 2; // 3rem * 2 for wrapper's border-radius
    const boundary =
      offsetHeight - barHeight - limit * parseFloat(rootFontSize);
    if (position > boundary) {
      position = boundary;
    }
    this.bar.style.top = `${position}px`;
  }

  _fadeInIndicator() {
    this.visibleIndicator = true;
  }

  _fadeOutIndicator() {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      this.visibleIndicator = false;
    }, 2000);
  }

  render() {
    const containerClasses = {
      container: true,
      show: this.open,
      hide: !this.open,
    };
    const messageClasses = {
      'subtitle-1': true,
      message: true,
      hide: !this.message,
    };
    const indicatorClasses = {
      indicator: true,
      hide: !this.isOverflow,
      visible: this.visibleIndicator,
    };
    return html`
      <div class="${classMap(containerClasses)}">
        <div
          class="dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog_label"
        >
          ${this.dialogtitle
            ? html`
                <div id="dialog_label" class="h3 title" dir="auto">
                  ${this.dialogtitle}
                </div>
              `
            : ''}
          <div class="wrapper">
            <div
              class="content"
              @scroll=${this._onScroll}
              @touchstart="${this._fadeInIndicator}"
              @touchend="${this._fadeOutIndicator}"
            >
              <div
                id="dialog_desc"
                class="${classMap(messageClasses)}"
                dir="auto"
              >
                ${this.message}
              </div>
              <slot name="custom-view"></slot>
            </div>
            <div class="${classMap(indicatorClasses)}">
              <div class="bar"></div>
            </div>
          </div>
          <div class="buttons">
            ${this.getButtons()}
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define(DialogElement.is, DialogElement);
